/**
 * ANALYTICS_OPT_OUT_COOKIE_NAME is the cookie that carries the opt-out between
 * Mountaya's properties. It mirrors the localStorage key deliberately: one
 * decision, one name, wherever it is written. A dot is legal here — RFC 6265
 * defines a cookie name as a token, which excludes the separators but not `.`.
 */
const ANALYTICS_OPT_OUT_COOKIE_NAME = "mountaya.analytics.opt_out";

/**
 * OPTED_OUT is the only value ever written, matching the storage marker for the
 * same reason it exists there: a hand-edited value must not read as an opt-out.
 */
const OPTED_OUT = "1";

/**
 * COOKIE_MAX_AGE_SECONDS is 400 days, the ceiling browsers clamp a cookie
 * lifetime to. This is the shorter half of the pair — the localStorage memo does
 * not expire — and it costs nothing: a person still signing in after that long is
 * reclassified the moment their session resolves.
 */
const COOKIE_MAX_AGE_SECONDS = 34_560_000;

/**
 * ALREADY_EXPIRED is the Max-Age that deletes a cookie rather than storing one.
 *
 * It is negative rather than zero. RFC 6265 expires a cookie at any Max-Age at or
 * below zero, but zero puts the expiry on the very instant it is written, and an
 * implementation comparing the two as "expires after now" keeps the cookie —
 * happy-dom does exactly that, so a deletion written as zero is a coin flip. A
 * negative value is past by construction, and the grammar admits it: the spec
 * names `-` alongside the digits as a legal first character.
 */
const ALREADY_EXPIRED = -1;

/**
 * CookieOptions are what the serializer varies. domain is the empty string for a
 * host-only cookie, maxAge distinguishes a write from a deletion, and secure
 * tracks the scheme the page was served over.
 */
interface CookieOptions {
  domain: string;
  maxAge: number;
  secure: boolean;
}

/**
 * resolveCookieDomain returns the domain the opt-out cookie is scoped to, derived
 * from the application's own origin by dropping its first label:
 * `https://app.mountaya.com` yields `mountaya.com`, which the documentation site
 * at `docs.mountaya.com` is under and can therefore read.
 *
 * Deriving it beats configuring it. The origin is already required configuration
 * the application cannot boot without, so there is no second value to set at
 * deploy time and none to forget — and a forgotten one would silently leave the
 * exclusion host-scoped, which is the exact failure this cookie exists to fix.
 *
 * Three shapes resolve to the empty string, meaning a host-only cookie: an origin
 * that does not parse, a hostname with no dot (`localhost`, where a host-only
 * cookie is already right because cookies ignore ports and the documentation dev
 * server is a second port on the same host), and an IP literal. So does a parent
 * that is itself a single label, because a browser rejects a cookie scoped to a
 * bare public suffix outright — the same guard the Ory session cookie applies in
 * the host's own configuration.
 *
 * It resolves rather than throws. A misderived domain costs the cross-property
 * half of the exclusion; analytics is never allowed to surface as an application
 * error.
 */
export const resolveCookieDomain = (origin: string): string => {
  const hostname = hostnameOf(origin);
  if (hostname === undefined || !hostname.includes(".") || isIpLiteral(hostname)) {
    return "";
  }

  const parent = hostname.slice(hostname.indexOf(".") + 1);

  return parent.includes(".") ? parent : "";
};

/**
 * hasOptOutCookie reports whether a serialized cookie header carries the marker.
 *
 * The name is compared exactly rather than searched for, so a cookie that merely
 * contains this one's name — `x-mountaya.analytics.opt_out`, or a value that
 * happens to spell it — does not read as an opt-out.
 */
export const hasOptOutCookie = (cookie: string): boolean =>
  cookie.split(";").some((pair) => {
    const separator = pair.indexOf("=");
    if (separator === -1) {
      return false;
    }

    return (
      pair.slice(0, separator).trim() === ANALYTICS_OPT_OUT_COOKIE_NAME &&
      pair.slice(separator + 1).trim() === OPTED_OUT
    );
  });

/**
 * readOptOutCookie reports whether this browser carries the opt-out marker. It is
 * what makes the exclusion hold on a property that has no identity of its own: the
 * documentation site never learns who is reading it, but it can see what the
 * application wrote on the shared parent domain.
 *
 * A missing document (a build step importing this module) or a throwing read reads
 * as "not opted out", matching the storage memo: the failure costs a session, not
 * correctness.
 */
export const readOptOutCookie = (): boolean => {
  try {
    return hasOptOutCookie(globalThis.document?.cookie ?? "");
  } catch {
    return false;
  }
};

/**
 * writeOptOutCookie persists the opt-out where every Mountaya property can read
 * it. Best-effort, like the storage memo it accompanies: a browser refusing
 * cookies still has the local half.
 */
export const writeOptOutCookie = (domain: string): void => {
  setCookie({ domain, maxAge: COOKIE_MAX_AGE_SECONDS, secure: isSecureOrigin() });
};

/**
 * deleteOptOutCookie readmits this browser across every property.
 *
 * It goes through the same serializer as the write, which is load-bearing rather
 * than tidy. A browser only replaces a cookie when the name, Domain and Path all
 * match, so a deletion that omitted the Domain would expire a host-only cookie
 * that was never there and leave the parent-domain one untouched — silencing this
 * browser permanently, on the application as much as on the documentation.
 */
export const deleteOptOutCookie = (domain: string): void => {
  setCookie({ domain, maxAge: ALREADY_EXPIRED, secure: isSecureOrigin() });
};

/**
 * setCookie is the single seam onto document.cookie, so a write and a deletion
 * cannot disagree about how the cookie is addressed.
 */
const setCookie = (options: CookieOptions): void => {
  try {
    if (globalThis.document) {
      // biome-ignore lint/suspicious/noDocumentCookie: CookieStore, the API the rule points at, is asynchronous and unsupported in Firefox; these helpers are synchronous because the gate that reads them is.
      globalThis.document.cookie = serializeCookie(options);
    }
  } catch {
    // Cookies unavailable (a hardened browser, a sandboxed frame): the storage
    // memo still holds this device's decision on its own origin.
  }
};

/**
 * serializeCookie renders the cookie in a fixed attribute order.
 *
 * SameSite is Lax because nothing reads this cookie off a request — it exists for
 * first-party script on a Mountaya page — and Secure tracks the scheme rather than
 * being pinned on, because Safari rejects a Secure cookie over `http://localhost`
 * and pinning it would leave the exclusion untestable in development.
 */
const serializeCookie = ({ domain, maxAge, secure }: CookieOptions): string => {
  const attributes = [
    `${ANALYTICS_OPT_OUT_COOKIE_NAME}=${OPTED_OUT}`,
    "Path=/",
    `Max-Age=${maxAge}`,
    "SameSite=Lax",
  ];

  if (domain !== "") {
    attributes.push(`Domain=${domain}`);
  }

  if (secure) {
    attributes.push("Secure");
  }

  return attributes.join("; ");
};

/**
 * hostnameOf returns an origin's host, or undefined when the value does not parse
 * as a URL.
 */
const hostnameOf = (origin: string): string | undefined => {
  try {
    return new URL(origin).hostname;
  } catch {
    return undefined;
  }
};

/**
 * isIpLiteral reports whether a hostname is an IPv4 address, whose dots separate
 * octets rather than labels and so have no parent to derive. IPv6 literals are
 * bracketed and carry no dot, so the caller's own check already covers them.
 */
const isIpLiteral = (hostname: string): boolean => /^[0-9.]+$/.test(hostname);

/**
 * isSecureOrigin reports whether the page was served over HTTPS, which is the
 * only context a Secure cookie survives.
 */
const isSecureOrigin = (): boolean => globalThis.location?.protocol === "https:";
